import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';

import Post from '../postComponent/postComponent';
import {
  Stack,
  Select,
  Box,
  SelectChangeEvent,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Chip,
  Skeleton,
  ToggleButtonGroup,
  ToggleButton,
  Button,
} from '@mui/material';
import { PostData, FeedData, FollowData } from '../model/types';
import { CacheContext, CacheContextType } from '../context/CacheContext';
import { AuthContext, MyContext } from '../context/compatibility';
import { supabase } from '../supabaseClient';
import Follow from '../Follows/FollowComponent';
import { DateTime } from 'luxon';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';

type SortMode = 'latest' | 'hot' | 'expiring';

function hotScore(post: PostData): number {
  const ageHours = DateTime.now().diff(DateTime.fromISO(post.created_at)).as('hours');
  return (post.vote_count ?? 0) / Math.pow(ageHours + 2, 1.5);
}

function Feed({
  userRecordIDs,
  showMenu,
  feedRecord,
  hideExpiredByDefault = true,
}: {
  userRecordIDs?: string[];
  showMenu?: boolean;
  feedRecord?: FeedData;
  // Home/feed pages default to hiding expired posts (user can uncheck).
  // User-profile pages pass `false` so dead posts are visible and
  // revivable inline.
  hideExpiredByDefault?: boolean;
}) {
  const [feedType, setFeedType] = useState('all');
  const [sortMode, setSortMode] = useState<SortMode>('latest');
  const { loadMorePosts, hasMorePosts, loadingMorePosts } = useContext<CacheContextType>(CacheContext);
  // Observer lives in a ref so the callback-ref below can tear it down
  // when the sentinel element unmounts (which happens when the feed
  // flips between empty/non-empty branches or when posts repopulate).
  const observerRef = useRef<IntersectionObserver | null>(null);
  const getPossiblePosts = useContext<CacheContextType>(CacheContext).allPosts;
  const getAllFollows = useContext<CacheContextType>(CacheContext).allFollows;
  const getAllFeedTags = useContext<CacheContextType>(CacheContext).allFeedTags;
  const getFollow = useContext<CacheContextType>(CacheContext).getFollow;
  const getPost = useContext<CacheContextType>(CacheContext).getPost;
  const [posts, setPosts] = useState<Array<JSX.Element>>([]);
  // Hide expired by default on the home feed. Users can uncheck
  // "Hide expired" to see dead posts inline and vote them back.
  // Profile pages pass hideExpiredByDefault=false to show dead posts.
  const [filterExpired, setFilterExpired] = useState(hideExpiredByDefault);
  // Graveyard needs DB-direct queries — expired posts are never in the
  // CacheProvider's rolling newest-50 window, so filtering the cache
  // by `expire_at < now` always returns nothing. We fetch them here
  // instead, scoped to "pre-wipe" = still in the table but past due.
  const [graveyardPosts, setGraveyardPosts] = useState<PostData[] | null>(null);
  const [graveyardLoading, setGraveyardLoading] = useState(false);
  const [showNewBanner, setShowNewBanner] = useState(false);
  const prevPostsLengthRef = useRef(0);
  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  const { posts: postsLoading } = useContext<CacheContextType>(CacheContext).loadingStatus;

  function findParent(
    stackPost: PostData | FollowData,
    expandToReply?: string,
  ): any {
    if (stackPost === undefined) return;
    if (!expandToReply) expandToReply = stackPost.id;
    if (stackPost.parent_type === 'follows') {
      if (getFollow(stackPost.parent_id ?? '') === undefined) return;
      return {
        post: (
          <Follow
            follow={getFollow(stackPost.parent_id ?? '')}
            useParentID={false}
            expandToReply={expandToReply}
            key={stackPost.parent_id}
          />
        ),
        id: stackPost.parent_id,
      };
    } else if (
      stackPost.parent_id !== undefined &&
      stackPost.parent_id !== null
    ) {
      return findParent(
        getPost(stackPost?.parent_id ?? '') as PostData,
        expandToReply,
      );
    }
    return {
      post: (
        <Post
          post={stackPost}
          expandToReply={expandToReply}
          key={expandToReply}
        />
      ),
      id: stackPost.id,
    };
  }

  function sortPostsAndExpandReplies(posts: PostData[]) {
    // Buried capsules are now DISCOVERABLE so others can donate time
    // to crack them open. They still appear in feeds — the post card
    // itself masks the content client-side.
    const visiblePosts = posts;

    // Effective timestamp for sorting: when a capsule opens, its
    // effective_at is the open_at moment, so it surfaces in "Latest"
    // like a freshly-posted item. Still-buried capsules sort by
    // created_at so they don't dominate the top of the feed before
    // they're actually open.
    const effectiveAt = (p: PostData) => {
      if (p.open_at) {
        const openDt = DateTime.fromISO(p.open_at);
        // Opened: use open_at (freshly surfaces at top)
        if (openDt <= DateTime.now()) return openDt.toSeconds();
        // Still buried: sort by creation so it doesn't jump to top
        return DateTime.fromISO(p.created_at).toSeconds();
      }
      return DateTime.fromISO(p.created_at).toSeconds();
    };

    let sortedPosts: PostData[];
    if (sortMode === 'hot') {
      sortedPosts = [...visiblePosts].sort((a, b) => hotScore(b) - hotScore(a));
    } else if (sortMode === 'expiring') {
      sortedPosts = [...visiblePosts]
        .filter((p) => !p.is_expired && !p.is_permanent)
        .sort((a, b) =>
          DateTime.fromISO(a.expire_at).toSeconds() - DateTime.fromISO(b.expire_at).toSeconds()
        );
    } else {
      sortedPosts = [...visiblePosts].sort((a, b) => effectiveAt(b) - effectiveAt(a));
    }
    let parentIDs: string[] = [];
    let returnPosts: Array<JSX.Element> = [];
    sortedPosts.forEach((post) => {
      if (post.parent_id === null) {
        parentIDs.push(post.id);
      }
    });

    sortedPosts.forEach((post) => {
      if (post.parent_id === null) {
        returnPosts.push(<Post post={post} key={post.id} />);
      } else {
        let parent = findParent(getPost(post.id) as PostData);

        if (parent === undefined) return;

        if (!parentIDs.includes(parent.id)) {
          parentIDs.push(parent.id);
          returnPosts.push(parent.post);
        }
      }
    });

    return returnPosts;
  }

  useEffect(() => {
    const possiblePosts = getPossiblePosts();
    let displayPosts = possiblePosts;

    if (feedRecord !== undefined) setFeedType('feed');
    else if (userRecordIDs) setFeedType('user');

    switch (feedType) {
      default:
        displayPosts = possiblePosts;
        break;
      case 'user':
        displayPosts = possiblePosts.filter((post) =>
          userRecordIDs?.includes(post.user_record_id),
        );
        break;
      case 'feed':
        if (feedRecord) {
          const feedPostIds = new Set(
            getAllFeedTags()
              .filter((ft) => ft.feed_id === feedRecord.id && ft.object_type === 'posts')
              .map((ft) => ft.object_id),
          );
          displayPosts = possiblePosts.filter((p) => feedPostIds.has(p.id));
        }
        break;
      case 'follows':
        // Show posts from users the current user is actively following
        const myFollows = getAllFollows().filter(
          (f) =>
            f.user_record_id === userRecord?.id &&
            !f.is_expired &&
            DateTime.fromISO(f.expire_at) > DateTime.now() &&
            f.parent_type === 'users',
        );
        const followedUserIDs = new Set(myFollows.map((f) => f.parent_id));
        displayPosts = possiblePosts.filter((post) =>
          followedUserIDs.has(post.user_record_id),
        );
        break;
      case 'graveyard':
        setFilterExpired(false);
        // Use the DB-direct fetch below. Start with an empty list so
        // we don't flash stale cache posts while the query runs.
        displayPosts = graveyardPosts ?? [];
        break;
      case 'sparks':
        // Firehose of sparks — only live, non-expired bursts.
        displayPosts = possiblePosts.filter(
          (post) =>
            post.is_spark === true &&
            !post.is_expired &&
            DateTime.fromISO(post.expire_at) > DateTime.now(),
        );
        break;
    }
    if (filterExpired) {
      displayPosts = displayPosts.filter(
        (post) => DateTime.fromISO(post.expire_at) > DateTime.now(),
      );
    }
    setPosts(sortPostsAndExpandReplies(displayPosts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRecordIDs, feedType, sortMode, getPossiblePosts, filterExpired, getAllFeedTags, feedRecord, graveyardPosts]);

  // Pull dead-but-not-yet-wiped posts directly from Supabase. Runs
  // whenever the user opens the Graveyard feed. `is_expired = true` OR
  // `expire_at < now()` captures both the row the monthly wipe will
  // soon delete and anything whose timer just ran out between wipes.
  useEffect(() => {
    if (feedType !== 'graveyard') return;
    let cancelled = false;
    setGraveyardLoading(true);
    supabase
      .from('posts')
      .select('*')
      .eq('is_spark', false)
      .eq('is_permanent', false)
      .or(`is_expired.eq.true,expire_at.lt.${new Date().toISOString()}`)
      .order('expire_at', { ascending: false })
      .limit(200)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data) setGraveyardPosts(data as PostData[]);
        else setGraveyardPosts([]);
        setGraveyardLoading(false);
      });
    return () => { cancelled = true; };
  }, [feedType]);

  const handleChange = (event: SelectChangeEvent) => {
    setFeedType(event.target.value as string);
  };

  // Detect new posts arriving while user is scrolled down
  useEffect(() => {
    if (posts.length > prevPostsLengthRef.current && prevPostsLengthRef.current > 0 && window.scrollY > 300) {
      setShowNewBanner(true);
    }
    prevPostsLengthRef.current = posts.length;
  }, [posts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onScroll = () => { if (window.scrollY < 100) setShowNewBanner(false); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Infinite scroll — keep the freshest values in a ref so the
  // callback-ref observer (set up once per sentinel mount) always
  // reads the current hasMore/loading flags without being recreated.
  const scrollStateRef = useRef({ hasMorePosts, loadingMorePosts, loadMorePosts });
  scrollStateRef.current = { hasMorePosts, loadingMorePosts, loadMorePosts };

  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    // Tear down any previous observer when the node changes/unmounts.
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const s = scrollStateRef.current;
        if (entries[0].isIntersecting && s.hasMorePosts && !s.loadingMorePosts) {
          s.loadMorePosts();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return (
    <Box width="100%">
      {showMenu && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Select
                value={feedType}
                onChange={handleChange}
                size="small"
                sx={{ minWidth: 130 }}
              >
                <MenuItem value="all">All Posts</MenuItem>
                <MenuItem value="follows">Following</MenuItem>
                <MenuItem value="sparks">⚡ Sparks</MenuItem>
                <MenuItem value="graveyard">The Graveyard</MenuItem>
              </Select>
              {feedType !== 'graveyard' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={filterExpired}
                      onChange={() => setFilterExpired((prev) => !prev)}
                      size="small"
                    />
                  }
                  label={<Typography variant="caption">Hide expired</Typography>}
                  sx={{ ml: 0 }}
                />
              )}
            </Box>
            <Chip
              label="LIVE"
              size="small"
              color="success"
              variant="outlined"
              sx={{
                fontFamily: '"VT323", monospace',
                letterSpacing: 2,
                fontSize: '0.85rem',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.4 },
                },
              }}
            />
          </Box>

          {/* Sort mode toggle */}
          {feedType !== 'graveyard' && (
            <ToggleButtonGroup
              value={sortMode}
              exclusive
              onChange={(_, v) => v && setSortMode(v)}
              size="small"
              sx={{ '& .MuiToggleButton-root': { py: 0.4, px: 1.25, fontSize: '0.75rem', textTransform: 'none' } }}
            >
              <ToggleButton value="latest">
                <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5 }} />
                Latest
              </ToggleButton>
              <ToggleButton value="hot">
                <WhatshotIcon sx={{ fontSize: 14, mr: 0.5 }} />
                Hot
              </ToggleButton>
              <ToggleButton value="expiring">
                <HourglassBottomIcon sx={{ fontSize: 14, mr: 0.5 }} />
                Expiring
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      )}

      {/* New posts banner — appears when Realtime pushes new content while scrolled down */}
      {showNewBanner && (
        <Box
          onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setShowNewBanner(false); }}
          sx={{
            position: 'sticky', top: 68, zIndex: 100, mb: 1.5, cursor: 'pointer',
            bgcolor: 'primary.main', color: 'white', borderRadius: 2,
            py: 0.75, px: 2, textAlign: 'center',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            animation: 'slideDown 0.25s ease',
            '@keyframes slideDown': { from: { opacity: 0, transform: 'translateY(-8px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
            ↑ New posts — tap to see them
          </Typography>
        </Box>
      )}

      {postsLoading || (feedType === 'graveyard' && graveyardLoading) ? (
        <Stack spacing={1.5}>
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderLeft: '3px solid', borderLeftColor: 'divider', borderRadius: '6px', p: 1.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Skeleton variant="circular" width={52} height={52} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="30%" height={20} sx={{ mb: 0.5 }} />
                  <Skeleton width="90%" height={16} />
                  <Skeleton width="75%" height={16} />
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : posts.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 96,
              height: 96,
              border: '2px dashed',
              borderColor: 'divider',
              bgcolor: 'transparent',
              mb: 2,
              fontSize: 48,
            }}
          >
            {feedType === 'graveyard' ? '⚰' :
             feedType === 'follows' ? '👁' :
             feedType === 'sparks' ? '⚡' :
             '∅'}
          </Box>
          <Typography
            variant="h5"
            sx={{
              mb: 0.5,
              fontFamily: '"VT323", monospace',
              letterSpacing: 3,
              // Use solid theme colors instead of opacity-multiplied hex so axe
              // contrast checks see the rendered ratio cleanly. The graveyard
              // and main empty states get a consistent muted-but-readable token.
              color: feedType === 'sparks' ? 'warning.main' : 'text.primary',
            }}
          >
            {feedType === 'graveyard' ? '[ GRAVEYARD EMPTY ]' :
             feedType === 'follows' ? '[ NOTHING FROM FOLLOWS ]' :
             feedType === 'sparks' ? '[ NO SPARKS FIRING ]' :
             '[ NO POSTS YET ]'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
            {feedType === 'graveyard'
              ? 'Posts that ran out of time will rest here when they fall.'
              : feedType === 'follows'
              ? 'Follow some users to see their posts here.'
              : feedType === 'sparks'
              ? 'Sparks are zero-cost 5-minute bursts.'
              : 'Be the first to post something.'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
            {feedType === 'graveyard'
              ? 'Time is undefeated.'
              : feedType === 'sparks'
              ? 'Fire one from the composer ⚡'
              : feedType === 'follows'
              ? 'Find people on the leaderboard or feeds page.'
              : 'Time is an economy — make your first deposit.'}
          </Typography>
          {/* Default home-feed empty state gets a Compose CTA that scrolls
              back to the composer at the top of the page. Skipped for
              graveyard/sparks/follows where the user has nothing to compose. */}
          {!['graveyard', 'sparks', 'follows'].includes(feedType) && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              sx={{
                mt: 2,
                borderWidth: 2,
                borderColor: 'currentColor',
                fontFamily: '"VT323", monospace',
                letterSpacing: 2,
                '&:hover': { borderWidth: 2, borderColor: 'currentColor' },
              }}
            >
              [ COMPOSE → ]
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Stack spacing={1.5} sx={{ width: '100%' }}>
            {posts}
          </Stack>

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} />
          {loadingMorePosts && (
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              {[1, 2].map((i) => (
                <Box key={i} sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderLeft: '3px solid', borderLeftColor: 'divider', borderRadius: '6px', p: 1.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Skeleton variant="circular" width={52} height={52} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="30%" height={20} sx={{ mb: 0.5 }} />
                      <Skeleton width="90%" height={16} />
                      <Skeleton width="60%" height={16} />
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
          {!hasMorePosts && posts.length > 0 && (
            <Box sx={{ textAlign: 'center', py: 3, color: 'text.disabled', fontFamily: '"VT323", monospace', letterSpacing: 1, fontSize: '0.9rem' }}>
              [ END OF FEED ]
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default Feed;
